import React, { useEffect, useState } from 'react';
import { Modal } from '@components/molecules/Modal';
import { DiscountToggle } from '@components/molecules/DiscountToggle';
import { computePerLineDiscount, DiscountInput } from '@utils/discount';

export interface InvoiceLineDiscountTarget {
  /** Source-entity-id of the cart row, used by the parent to update the right entry. */
  sourceEntityId: string;
  description: string;
  quantity: number;
  unitPrice: number;
  /** Currently persisted discount on the cart row (USD dollars). 0 = no discount. */
  currentDiscountAmount: number;
}

interface InvoiceLineDiscountModalProps {
  isOpen: boolean;
  onClose: () => void;
  target: InvoiceLineDiscountTarget | null;
  /** Called when the user clicks "Apply discount". Passes the new total dollar discount. */
  onApply: (discountAmount: number) => void;
  /** Called when the user clicks "Remove discount". */
  onRemove: () => void;
}

const formatCurrency = (amount: number, currency = 'USD'): string => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

/**
 * Per-invoice-line discount editor. Lives entirely client-side: the parent owns
 * the cart entry and just receives `discountAmount` (USD total for the line) on
 * apply / 0 on remove. Persistence happens through the existing draft autosave
 * flow once the parent writes the value into the cart state.
 */
export const InvoiceLineDiscountModal: React.FC<InvoiceLineDiscountModalProps> = ({
  isOpen,
  onClose,
  target,
  onApply,
  onRemove,
}) => {
  // We persist the total USD discount on `invoice_line_item.discountAmount`. In
  // per-line scope that value maps 1:1 back to the USD input — type/value intent
  // for PERCENT is lost across saves, so reopening always rehydrates as USD.
  const [discount, setDiscount] = useState<DiscountInput>({ type: null, value: 0 });

  useEffect(() => {
    if (!isOpen || !target) return;
    if (target.currentDiscountAmount > 0) {
      setDiscount({ type: 'USD', value: target.currentDiscountAmount });
    } else {
      setDiscount({ type: null, value: 0 });
    }
  }, [isOpen, target]);

  if (!target) return null;

  const handleApply = () => {
    const { discountAmount } = computePerLineDiscount(
      target.unitPrice,
      target.quantity,
      discount.type,
      discount.value,
      'per-line',
    );
    onApply(discountAmount);
  };

  const handleRemove = () => {
    setDiscount({ type: null, value: 0 });
    onRemove();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Line discount" size="small">
      <div className="space-y-4">
        <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 space-y-1">
          <p className="text-sm font-medium text-gray-900">{target.description}</p>
          <p className="text-xs text-gray-600">
            {target.quantity} × {formatCurrency(target.unitPrice)} ={' '}
            <span className="font-medium text-gray-900">
              {formatCurrency(target.unitPrice * target.quantity)}
            </span>
          </p>
        </div>

        <DiscountToggle
          value={discount}
          onChange={setDiscount}
          unitPrice={target.unitPrice}
          quantity={target.quantity}
          scope="per-line"
          helper="Applied only to this invoice. $ is the total dollar discount for the line; % is a percentage of the line total."
        />

        <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
          {target.currentDiscountAmount > 0 ? (
            <button
              type="button"
              onClick={handleRemove}
              className="px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 rounded-md">
              Remove discount
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-900 rounded-md hover:bg-blue-800">
              Apply discount
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
